import React from "react";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../../app/store/slices/uiSlice";

const LoadingSpinner = () => (
  <svg
    className="animate-spin h-5 w-5 text-blue-500"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export default function FormSelect({
  label,
  name,
  value,
  onChange,
  options = [],
  required = false,
  disabled = false,
  loading = false,
  placeholder = "Select an option",
  error = "",
  className = "",
  hideLabel = true,
  ...props
}) {
  const darkMode = useSelector(selectDarkMode);

  const baseClass = "w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 transition-colors";
  const borderClass = error
    ? "border-red-500 focus:ring-red-500"
    : darkMode
      ? "border-gray-600 focus:ring-blue-500 bg-gray-700 text-white"
      : "border-gray-300 focus:ring-blue-500 bg-white";

  const isDisabled = disabled || loading;
  const disabledClass = isDisabled
    ? darkMode ? "bg-gray-800 cursor-not-allowed text-gray-400" : "bg-gray-100 cursor-not-allowed"
    : "";

  return (
    <div className="form-group">
      {!hideLabel && label && (
        <label htmlFor={name} className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-200' : 'text-[#232526]'}`}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          disabled={isDisabled}
          className={`${baseClass} ${borderClass} ${disabledClass} ${className} ${loading ? 'pr-10' : ''}`}
          {...props}
        >
          {loading ? (
            <option value="" disabled className={darkMode ? 'bg-gray-700 text-gray-400' : ''}>
              Loading...
            </option>
          ) : options.length === 0 ? (
            <option value="" disabled className={darkMode ? 'bg-gray-700 text-gray-400' : ''}>
              No data available
            </option>
          ) : (
            <>
              {placeholder && (
                <option value="" disabled className={darkMode ? 'bg-gray-700 text-gray-400' : ''}>
                  {placeholder}
                </option>
              )}
              {options.map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled} className={darkMode ? 'bg-gray-700 text-white' : ''}>
                  {option.label}
                </option>
              ))}
            </>
          )}
        </select>

        {loading && (
          <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none">
            <LoadingSpinner />
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  );
}
